import { useState } from 'react';

const ErrorFallback = ({ error, errorInfo, onReset }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Card principale */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          {/* Icône et titre */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Oups ! Une erreur est survenue
            </h1>
            <p className="text-gray-600 text-lg">
              Quelque chose s'est mal passé. Ne vous inquiétez pas, vos données sont en sécurité.
            </p>
          </div>

          {/* Message d'erreur simplifié */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-red-800">
                    {error.toString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button
              onClick={onReset}
              className="flex-1 bg-gradient-to-r from-sky-600 to-sky-400 text-white px-6 py-3 rounded-xl font-semibold hover:from-sky-700 hover:to-sky-500 transition-all shadow-lg hover:shadow-xl"
            >
              Retour à l'accueil
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
            >
              Recharger la page
            </button>
          </div>

          {/* Toggle détails techniques */}
          <div className="border-t pt-6">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span className="text-sm font-medium">
                {showDetails ? 'Masquer les détails techniques' : 'Afficher les détails techniques'}
              </span>
              <svg
                className={`w-5 h-5 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Détails techniques (collapsible) */}
            {showDetails && (
              <div className="mt-4 bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-64">
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-400 mb-1">ERROR MESSAGE:</p>
                  <pre className="text-xs text-red-400 whitespace-pre-wrap break-words">
                    {error && error.toString()}
                  </pre>
                </div>
                {errorInfo && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 mb-1">COMPONENT STACK:</p>
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Si le problème persiste, veuillez contacter le support technique.
            </p>
          </div>
        </div>

        {/* Suggestions */}
        <div className="mt-6 bg-white bg-opacity-60 backdrop-blur rounded-2xl p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Que faire ensuite ?</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-sky-600 mr-2">•</span>
              <span>Essayez de recharger la page</span>
            </li>
            <li className="flex items-start">
              <span className="text-sky-600 mr-2">•</span>
              <span>Retournez à la page d'accueil et réessayez votre action</span>
            </li>
            <li className="flex items-start">
              <span className="text-sky-600 mr-2">•</span>
              <span>Vérifiez votre connexion internet</span>
            </li>
            <li className="flex items-start">
              <span className="text-sky-600 mr-2">•</span>
              <span>Si l'erreur persiste, contactez l'administrateur</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
